import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { NumericField } from "../../../controls";
import { ControlGroup } from "../views/ControlGroup";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";
import { ApplicationTooltip } from "../../../views";
import { useState } from "react";
import { OrderDetailsState } from "./OrderDetails";
import { ContractDetailsState } from "./ContractDetails";
import { useBinanceOptionsSecuredDataService } from "../../../../binance/hooks/binance-options-data-service/useBinanceOptionsSecuredDataService";
import { assetService } from "../../../../../shared/services/AssetService";
import { oderManagerUtils } from "../utils/OrderManagerUtils";
import { OptionInfoDto } from "../../../../../features/options-table/types/OptionInfoDto";
import { dateTimeConversionService } from "../../../../../shared/services/DateTimeConversionService";

const ABSENT_DATA_TEXT = "Absent data";
const ORDER_BOOK_DATE_TIME_FORMAT = "DD.MMM HH:mm:ss.SSS";

type BinanceOrderManagerProps = {
    contractDetails: ContractDetailsState;
    orderDetails: OrderDetailsState;
    optionInfo: OptionInfoDto | null;
    handleOrderDetailsChange: (patch: Partial<OrderDetailsState>) => void;
    handleSubmit: () => Promise<void>;
    onAutoOrderClick: () => void;
}

export function BinanceOrderManager({
    contractDetails,
    orderDetails,
    optionInfo,
    handleOrderDetailsChange,
    handleSubmit,
    onAutoOrderClick,
}: BinanceOrderManagerProps) {
    const {
        sendOptionOrder,
        cancelOptionOrder,
    } = useBinanceOptionsSecuredDataService();
    
    const [isBinanceCommandProcessing, setIsBinanceCommandProcessing] = useState<boolean>(false);
    
    const createOrderAndSetOrderId = async () => {
        const result = await sendOptionOrder(contractDetails.symbol, orderDetails.size, orderDetails.price, orderDetails.externalId);

        if (result !== null) {
            handleOrderDetailsChange({exchangeOrderId: result.orderId});
        }
    }

    const tryCancelOrder = async (): Promise<boolean> => {
        if (orderDetails.exchangeOrderId === null) return false;
        const result = await cancelOptionOrder(contractDetails.symbol, orderDetails.exchangeOrderId, orderDetails.externalId);
        return result !== null;
    }

    const isNewOrder = orderDetails.externalId === 0;
    
    const handleAddOrUpdateOrderOnBinance = async () => {
        setIsBinanceCommandProcessing(true);

        if (isNewOrder) return;

        if (orderDetails.exchangeOrderId === null){
            await createOrderAndSetOrderId();
        } else {
            if (await tryCancelOrder()){
                await createOrderAndSetOrderId();
            }
        }

        await handleSubmit();
        setIsBinanceCommandProcessing(false);
    }

    const handleCancelOrderOnBinance = async () => {
        setIsBinanceCommandProcessing(true);

        if (await tryCancelOrder()) {
            handleOrderDetailsChange({exchangeOrderId: null});
            await handleSubmit();
        }

        setIsBinanceCommandProcessing(false);
    }

    const isOptionReadyToSentToExchange =
        !isNewOrder &&
        assetService.isOptionType(contractDetails.assetType) &&
        contractDetails.expiration !== ' ' &&
        contractDetails.strike !== 0;

    const isAutoOrderAvailable =
        !isNewOrder &&
        assetService.isOptionType(contractDetails.assetType);

    const renderOrderBookSection = (
        price: number | null | undefined,
        size: number | null | undefined,
        color?: string,
        isPriceFirst = true,
    ) => {
        if (price === null || price === undefined || size === null || size === undefined) {
            return (
                <Box component="span" sx={{ color }}>
                    {ABSENT_DATA_TEXT}
                </Box>
            );
        }

        const priceText = `${numberFormatters.format0(price, numberFormatters.hideGroupDelimiterFormatOptions)} USDT`;
        const sizeText = `${numberFormatters.format2(size, numberFormatters.hideGroupDelimiterFormatOptions)} BTC`;
        
        return (
            <Box component="span" sx={{ color }}>
                {isPriceFirst
                    ? (
                        <>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                                {priceText}
                            </Box>
                            {` / ${sizeText}`}
                        </>
                    )
                    : (
                        <>
                            {`${sizeText} / `}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                                {priceText}
                            </Box>
                        </>
                    )}
            </Box>
        );
    };

    const orderBookUpdatedAtText = optionInfo?.OrderBookUpdatedAt === null ||
        optionInfo?.OrderBookUpdatedAt === undefined
        ? ABSENT_DATA_TEXT
        : dateTimeConversionService.convertDateToCustomFormattedString(
            optionInfo.OrderBookUpdatedAt,
            ORDER_BOOK_DATE_TIME_FORMAT);
    
    return (
        <ControlGroup title="Control orders on Binance">
            <Stack direction="column" spacing={0.75}>
                <Typography
                    component="div"
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        columnGap: "4px",
                        rowGap: "2px",
                        fontSize: "13px",
                    }}
                >
                    {renderOrderBookSection(
                        optionInfo?.BestBidPrice,
                        optionInfo?.BestBidSize,
                        "success.main",
                        false)}
                    <Box component="span">:</Box>
                    {renderOrderBookSection(
                        optionInfo?.BestAskPrice,
                        optionInfo?.BestAskSize,
                        "error.main")}
                    <Box component="span">:</Box>
                    <Box component="span">{orderBookUpdatedAtText}</Box>
                </Typography>
                <Stack direction="row" spacing={2}>
                    {orderDetails.exchangeOrderId === null
                        ?
                        <TextField
                            margin="normal"
                            type="string"
                            size="small"
                            label="Exchange order ID"
                            defaultValue="Not sent"
                            variant="standard"
                            sx={{
                                width: '105px'
                            }}
                        />
                        :
                        <NumericField
                            id="exchangeOrderId"
                            name="exchangeOrderId"
                            label="Exchange order ID"
                            value={orderDetails.exchangeOrderId}
                            formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                            width={105}
                            disabled={true}
                        />
                    }
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                        <ApplicationTooltip
                            title={
                                orderDetails.exchangeOrderId === null
                                    ? "Place a new order on Binance"
                                    : "Cancel the current Binance order and place a new one"
                            }
                        >
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleAddOrUpdateOrderOnBinance}
                                sx={{
                                    backgroundColor: theme => theme.palette.primary.light,
                                    "&:hover": {
                                        backgroundColor: theme => theme.palette.primary.main,
                                    },
                                }}
                                disabled={!isOptionReadyToSentToExchange || !orderDetails.isActive || isBinanceCommandProcessing}
                            >
                                {orderDetails.exchangeOrderId === null ? "Add" : "Update"}
                            </Button>
                        </ApplicationTooltip>
                        <ApplicationTooltip title="Cancel the current Binance order">
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleCancelOrderOnBinance}
                                sx={{
                                    backgroundColor: theme => theme.palette.error.light,
                                    "&:hover": {
                                        backgroundColor: theme => theme.palette.error.main,
                                    },
                                }}
                                disabled={
                                    !oderManagerUtils.isOptionCanBeCancelled(orderDetails) ||
                                    isBinanceCommandProcessing
                                }
                            >
                                Cancel
                            </Button>
                        </ApplicationTooltip>
                        <ApplicationTooltip title="Configure automatic option order execution">
                            <Button
                                size="small"
                                variant="contained"
                                onClick={onAutoOrderClick}
                                sx={{
                                    backgroundColor: theme => theme.palette.primary.light,
                                    "&:hover": {
                                        backgroundColor: theme => theme.palette.primary.main,
                                    },
                                }}
                                disabled={!isAutoOrderAvailable || isBinanceCommandProcessing}
                            >
                                Auto Order Manager
                            </Button>
                        </ApplicationTooltip>
                    </Stack>
                </Stack>
            </Stack>
        </ControlGroup>
    )
}
